import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import TermsOfService from '../../components/TermsOfService';
import { legalService, PLATFORM_SERVICE_CONTRACT_KEY } from '../../services/legalService';
import { validateCpfCnpj, formatCpfCnpjInput } from '../../utils/cpfCnpjValidator';

const InvoiceDataRequestPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Dados Fiscais', 'Contrato', 'Confirmação'];
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestData, setRequestData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    cpfCnpj: '',
  });
  
  const [formErrors, setFormErrors] = useState<any>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [contractViewed, setContractViewed] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState<{ key: string; version: string }[]>([]);
  const [signature, setSignature] = useState('');
  
  // Carregar dados da solicitação
  useEffect(() => {
    const fetchRequest = async () => {
      if (!token) {
        setError('Token inválido');
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get(`/invoice-data-request/${token}`);
        if (response.data.success) {
          setRequestData(response.data.data);
          if (response.data.data.cpfCnpj) {
            setFormData({ cpfCnpj: response.data.data.cpfCnpj });
            setActiveStep(1); // Pular para o passo do contrato se já tem CPF/CNPJ
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar solicitação');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRequest();
  }, [token]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'cpfCnpj') {
      const formattedValue = formatCpfCnpjInput(value);
      setFormData({ ...formData, [name]: formattedValue });
      
      // Validar CPF/CNPJ
      const validation = validateCpfCnpj(formattedValue);
      if (!validation.isValid && formattedValue.length > 0) {
        const cleanValue = formattedValue.replace(/\D/g, '');
        if (cleanValue.length < 11) {
          setFormErrors({ ...formErrors, cpfCnpj: 'CPF deve ter 11 dígitos' });
        } else if (cleanValue.length < 14) {
          setFormErrors({ ...formErrors, cpfCnpj: 'CNPJ deve ter 14 dígitos' });
        } else {
          setFormErrors({ ...formErrors, cpfCnpj: 'CPF/CNPJ inválido' });
        }
      } else {
        setFormErrors({ ...formErrors, cpfCnpj: null });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const handleNext = async () => {
    if (activeStep === 0) {
      // Validar CPF/CNPJ
      const validation = validateCpfCnpj(formData.cpfCnpj);
      if (!validation.isValid) {
        const cleanValue = formData.cpfCnpj.replace(/\D/g, '');
        let errorMessage = 'CPF/CNPJ inválido';
        if (cleanValue.length < 11) {
          errorMessage = 'CPF deve ter 11 dígitos';
        } else if (cleanValue.length < 14) {
          errorMessage = 'CNPJ deve ter 14 dígitos';
        }
        setFormErrors({ cpfCnpj: errorMessage });
        return;
      }
      
      // Submeter dados fiscais
      try {
        setSubmitting(true);
        await api.post(`/invoice-data-request/${token}/submit`, {
          cpfCnpj: formData.cpfCnpj,
        });
        setFormErrors({});
        setError(null);
        setActiveStep(1);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Erro ao salvar dados fiscais';
        setError(errorMessage);
        
        // Se o erro for de duplicata, mostrar também no campo
        if (errorMessage.includes('já está cadastrado') || errorMessage.includes('duplicado')) {
          setFormErrors({ cpfCnpj: 'Este CPF/CNPJ já está cadastrado em outra paróquia' });
        } else {
          setFormErrors({ cpfCnpj: errorMessage });
        }
      } finally {
        setSubmitting(false);
      }
    } else if (activeStep === 1) {
      // Validar assinatura e termos
      if (!signature.trim()) {
        setFormErrors({ signature: 'Nome do responsável é obrigatório' });
        return;
      }
      
      if (!contractViewed) {
        setFormErrors({ terms: 'Abra e leia o contrato antes de aceitar' });
        return;
      }
      
      if (!termsAccepted) {
        setFormErrors({ terms: 'Você deve aceitar os termos de uso para continuar' });
        return;
      }
      
      // Assinar contrato
      try {
        setSubmitting(true);
        await api.post(`/invoice-data-request/${token}/sign-contract`, {
          signature: signature.trim(),
          termsAccepted: true,
          contractViewed: true,
          legalAccepted,
        });
        setSuccess(true);
        setActiveStep(2);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao assinar contrato');
      } finally {
        setSubmitting(false);
      }
    }
  };
  
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };
  
  const handleShowTerms = () => {
    setShowTermsDialog(true);
  };
  
  const handleTermsAccept = async () => {
    try {
      const template = await legalService.getTemplate(PLATFORM_SERVICE_CONTRACT_KEY);
      setLegalAccepted([{ key: template.key, version: template.version }]);
    } catch {
      /* backend valida a versão */
    }
    setContractViewed(true);
    setTermsAccepted(true);
    setShowTermsDialog(false);
  };
  
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Dados Fiscais da Paróquia
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Para emitir a nota fiscal do evento <strong>"{requestData?.eventTitle}"</strong>, 
              precisamos do CPF ou CNPJ da paróquia.
            </Typography>
            
            <TextField
              fullWidth
              label="CPF ou CNPJ"
              name="cpfCnpj"
              value={formData.cpfCnpj}
              onChange={handleChange}
              error={!!formErrors.cpfCnpj}
              helperText={formErrors.cpfCnpj || 'Informe o CPF (11 dígitos) ou CNPJ (14 dígitos) da paróquia'}
              margin="normal"
              required
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Termos de Uso e Contrato de Prestação de Serviços
            </Typography>
            <Typography variant="body2" paragraph>
              Para finalizar, você precisa aceitar os termos de uso e o contrato de prestação de serviços. 
              Clique no botão abaixo para visualizar o contrato completo.
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleShowTerms}
                sx={{ minWidth: 200 }}
              >
                Visualizar Contrato
              </Button>
            </Box>
            
            <TextField
              fullWidth
              label="Nome do Responsável Legal"
              name="signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              error={!!formErrors.signature}
              helperText={formErrors.signature || 'Nome completo de quem está assinando o contrato'}
              margin="normal"
              required
              sx={{ mt: 3 }}
            />
            
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => {
                      if (!contractViewed) {
                        setFormErrors({ terms: 'Abra e leia o contrato antes de aceitar' });
                        return;
                      }
                      setTermsAccepted(e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Eu li e aceito os termos de uso e o contrato de prestação de serviços da ACUTIS TECNOLOGIA LTDA
                  </Typography>
                }
              />
              {formErrors.terms && (
                <Typography color="error" variant="caption" display="block">
                  {formErrors.terms}
                </Typography>
              )}
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Dados Enviados com Sucesso!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Os dados fiscais foram preenchidos e o contrato foi assinado com sucesso.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A nota fiscal poderá ser emitida em breve.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error && !requestData) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </Container>
    );
  }
  
  if (success && activeStep === 2) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
          {renderStepContent()}
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 8, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Preenchimento de Dados Fiscais
        </Typography>
        
        {requestData && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Evento:</strong> {requestData.eventTitle}
            </Typography>
            <Typography variant="body2">
              <strong>Paróquia:</strong> {requestData.parishName}
            </Typography>
          </Box>
        )}
        
        <Stepper activeStep={activeStep} sx={{ mt: 3, mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {renderStepContent()}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || submitting}
            onClick={handleBack}
          >
            Voltar
          </Button>
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : activeStep === 0 ? 'Continuar' : 'Assinar Contrato'}
            </Button>
          )}
        </Box>
      </Paper>
      
      <TermsOfService
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        onAccept={handleTermsAccept}
        parishName={requestData?.parishName || '[Nome da Paróquia]'}
        parishAddress="[Endereço da Paróquia]"
        parishCity="[Cidade]"
        parishState="[Estado]"
        responsibleName={signature || '[Nome do Responsável]'}
        responsibleEmail={requestData?.parishEmail || '[Email]'}
        responsibleCPF={formData.cpfCnpj || '[CPF/CNPJ]'}
      />
    </Container>
  );
};

export default InvoiceDataRequestPage;

